import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import set from "@rill/set";
import { getChartLabels } from "../../../utils/chart-labels";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { isPushOrPerformClient } from "../../../utils/user-helpers";

const {
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const {
    exercisesVolumeSplitCoreAccessory,
    query,
    search,
    feBranding = {},
  } = locals;
  const { period, units, mobileWV } = query;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolumeSplitCoreAccessory
      : _.pick(exercisesVolumeSplitCoreAccessory, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = getChartLabels(keys, query, period);
  const selectedMetricValues = _.values(selectedMetrics);

  const coreSeries = {
    name: "core",
    className: "core",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.core`),
        units
      );
    }),
  };

  const accessorySeries = {
    name: "accessory",
    className: "accessory",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.accessory`),
        units
      );
    }),
  };

  const totalSeries = _.zipWith(
    accessorySeries.data,
    coreSeries.data,
    (a = 0, b = 0) => a + b
  );
  const volumeSeries = _.concat(coreSeries, accessorySeries);
  const maxVolume = _.max(totalSeries);

  return (
    <main
      role="reports-wrapper"
      className="Main Metrics"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text={t("dashboard:metrics.calendarDayView")}
        classNames="mobile-hidden"
      />

      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("dashboard:metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />
        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/primary-secondary-vol-split-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/primary-secondary-vol-split-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("dashboard:coreAccessoryVolumeSplit.title")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("dashboard:coreAccessoryVolumeSplit.kg"), value: "metric" },
                  { text: t("dashboard:coreAccessoryVolumeSplit.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("dashboard:coreAccessoryVolumeSplit.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("dashboard:coreAccessoryVolumeSplit.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("dashboard:metrics.startDate")}
                value={query.start}
                max={query.end}
                // onChange={handleDayChange}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("dashboard:metrics.to")}</span>
              <DateInput
                name="end"
                placeholder={t("dashboard:metrics.endDate")}
                value={query.end}
                min={query.start}
                // onChange={handleDayChange}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("dashboard:metrics.month"), value: "month" },
                  { text: t("dashboard:metrics.week"), value: "week" },
                  { text: t("dashboard:metrics.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("dashboard:coreAccessoryVolumeSplit.kg"), value: "metric" },
                { text: t("dashboard:coreAccessoryVolumeSplit.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />

            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("dashboard:coreAccessoryVolumeSplit.total"), value: "total" },
                { text: t("dashboard:coreAccessoryVolumeSplit.squat"), value: "squat" },
                { text: t("dashboard:coreAccessoryVolumeSplit.bench"), value: "bench" },
                { text: t("dashboard:coreAccessoryVolumeSplit.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend">
                  <div className="bar series core">{t("dashboard:coreAccessoryVolumeSplit.primaryVolume")}</div>
                  <div className="bar series accessory">{t("dashboard:coreAccessoryVolumeSplit.secondaryVolume")}</div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("dashboard:coreAccessoryVolumeSplit.volume")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 80}px`,
                        height: "380px",
                        stackBars: true,
                        seriesBarDistance: 20,
                        chartPadding: { left: 35 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                          high: maxVolume,
                          low: 0,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const coreTotal =
                          _.round(_.get(coreSeries, ["data", i], 0), 1) || 0;
                        const accessoryTotal =
                          _.round(_.get(accessorySeries, ["data", i], 0), 1) ||
                          0;
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              <div>
                                {t("dashboard:coreAccessoryVolumeSplit.core")}{" "}
                                <b>{locals.withWeight(coreTotal, units)}</b>
                              </div>
                              <div>
                                {t("dashboard:coreAccessoryVolumeSplit.accessory")}{" "}
                                <b>
                                  {locals.withWeight(accessoryTotal, units)}
                                </b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const CoreAccessoryVolumeSplit = (app) => {
  app.get(
    "/core-accessory-volume-split",
    set("heading", "Primary & Secondary Volume Split"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolumeSplitCoreAccessory", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "core-accessory-volume-split",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolumeSplitCoreAccessory"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      const { activeUser: user } = locals;
      if (isPushOrPerformClient(user) || user.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default CoreAccessoryVolumeSplit;
